/** Retouren: Anmelden, Eingang mit Zustand A/B/C je Position, Erstattung freigeben (Leitung). */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RETOURE_STATUS, RETOURE_ZUSTAENDE, type RetoureZustand } from '@storeos/contracts';
import { auftrag, auftraege } from '../api/auftraege';
import { erstattungFreigeben, retoureAnmelden, retoureEingang, retouren, retourenlabelErzeugen, retourenlabelPfad, retourePruefung } from '../api/retouren';
import { DateiLink } from '../components/DateiLink';
import type { OrderDetail, RetoureZeile } from '../api/typen';
import { datum, euro, RETOURE_QUELLE_TEXT, RETOURE_TEXT } from '../components/format';
import { RetoureBadge } from '../components/Status';
import { Darf, Detailpanel, Dialog, DialogFormular, Feld, Filterleiste, Seitenkopf, Tabelle, useToast, Zustand } from '../components/ui';

const ZUSTAND_TEXT: Record<RetoureZustand, string> = { A: 'A – verkaufsfähig', B: 'B – Mangel', C: 'C – Ausschuss' };

export function Retouren() {
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const suche = sp.get('q') ?? '';
  const q = useQuery({ queryKey: ['retouren', status, suche], queryFn: () => retouren({ status: status || undefined, q: suche || undefined }) });
  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [anmelden, setAnmelden] = useState(false);
  const zeile = q.data?.eintraege.find((r) => r.id === auswahl) ?? null;
  return (
    <section className={zeile ? 'mit-panel' : ''}>
      <div className="hauptbereich">
        <Seitenkopf titel="Retouren"><button type="button" className="knopf" onClick={() => setAnmelden(true)} data-testid="retoure-anmelden">Retoure anmelden</button></Seitenkopf>
        <Filterleiste>
          {suche && <button type="button" className="knopf-flach" onClick={() => setSp(status ? { status } : {}, { replace: true })}>Suche „{suche}“ entfernen</button>}
          <select aria-label="Status" value={status} onChange={(e) => setSp(e.target.value ? { status: e.target.value } : {}, { replace: true })}>
            <option value="">Status: alle</option>
            {RETOURE_STATUS.map((s) => <option key={s} value={s}>{RETOURE_TEXT[s]}</option>)}
          </select>
        </Filterleiste>
        <Zustand laden={q.isLoading} fehler={q.error}>
          <Tabelle testid="retouren-tabelle" zeilen={q.data?.eintraege ?? []} schluessel={(r) => r.id} aktiv={auswahl} onZeile={(r) => setAuswahl(r.id)} leer="Keine Retouren"
            spalten={[
              { kopf: 'Nummer', zelle: (r) => r.nummer, klasse: 'mono' },
              { kopf: 'Auftrag', zelle: (r) => <Link to={`/auftraege/${r.order_id}`} className="mono" onClick={(e) => e.stopPropagation()}>{r.bestellnummer}</Link> },
              { kopf: 'Kunde', zelle: (r) => r.kunde_name ?? '–' },
              { kopf: 'Status', zelle: (r) => <RetoureBadge status={r.status} /> },
              { kopf: 'Angemeldet', zelle: (r) => datum(r.angemeldet_at) },
              { kopf: 'Frist', zelle: (r) => datum(r.frist_at) },
              { kopf: 'Erstattung', zelle: (r) => euro(r.erstattung_cents), rechts: true },
            ]} />
        </Zustand>
      </div>
      {zeile && <RetourePanel r={zeile} onSchliessen={() => setAuswahl(null)} />}
      <Dialog offen={anmelden} titel="Retoure anmelden" onSchliessen={() => setAnmelden(false)} breit>
        {anmelden && <AnmeldeFormular onAbbrechen={() => setAnmelden(false)} onFertig={(id) => { setAnmelden(false); setAuswahl(id); }} />}
      </Dialog>
    </section>
  );
}

function AnmeldeFormular({ onAbbrechen, onFertig }: { onAbbrechen: () => void; onFertig: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nr, setNr] = useState('');
  const [o, setO] = useState<OrderDetail | null>(null);
  const [menge, setMenge] = useState<Record<string, number>>({});
  const [grund, setGrund] = useState('');
  const [quelle, setQuelle] = useState<'widerruf' | 'manuell'>('widerruf');
  const [fehler, setFehler] = useState<string | null>(null);
  async function laden() {
    setFehler(null);
    const r = await auftraege({ q: nr.trim(), pro_seite: 5 });
    const z = r.eintraege.find((x) => x.bestellnummer === nr.trim());
    if (!z) { setFehler('Auftrag nicht gefunden'); return; }
    const d = await auftrag(z.id);
    setO(d); setMenge({});
  }
  if (!o) {
    return (
      <form className="formular" onSubmit={(e) => { e.preventDefault(); void laden(); }}>
        <Feld label="Bestellnummer"><input autoFocus value={nr} onChange={(e) => setNr(e.target.value)} data-testid="retoure-bestellnummer" /></Feld>
        {fehler && <p className="fehler">{fehler}</p>}
        <div className="knopfreihe"><button type="button" className="knopf-zweit" onClick={onAbbrechen}>Abbrechen</button><button type="submit" className="knopf">Auftrag laden</button></div>
      </form>
    );
  }
  return (
    <DialogFormular absendenText="Anmelden" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      const positionen = Object.entries(menge).filter(([, m]) => m > 0).map(([order_item_id, m]) => ({ order_item_id, menge: m }));
      if (!positionen.length) throw new Error('Mindestens eine Position wählen.');
      const r = await retoureAnmelden({ order_id: o.id, quelle, grund, positionen });
      toast(`Retoure ${r.nummer} angemeldet`);
      void qc.invalidateQueries({ queryKey: ['retouren'] });
      onFertig(r.id);
    }}>
      <p>Auftrag <strong className="mono">{o.bestellnummer}</strong> · {o.adressen.find((x) => x.typ === 'rechnung')?.nachname}</p>
      <table className="tabelle">
        <thead><tr><th>Position</th><th className="rechts">Bestellt</th><th className="rechts">Zurück</th></tr></thead>
        <tbody>
          {o.positionen.map((p) => (
            <tr key={p.id} data-testid="retoure-position" data-personalisiert={p.route === 'intern_bb' || p.personalisierung ? '1' : '0'}>
              <td>{p.name_snapshot}{p.variante_snapshot ? ` · ${p.variante_snapshot}` : ''}{p.route === 'intern_bb' && <small className="warn-text"> personalisiert: nur Reklamation</small>}</td>
              <td className="rechts">{p.menge}</td>
              <td className="rechts"><input type="number" min={0} max={p.menge} value={menge[p.id] ?? 0} onChange={(e) => setMenge({ ...menge, [p.id]: Math.min(p.menge, Math.max(0, Number(e.target.value))) })} style={{ width: '4.5rem' }} data-testid="retoure-menge" /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Feld label="Art">
        <select value={quelle} onChange={(e) => setQuelle(e.target.value as 'widerruf' | 'manuell')} data-testid="retoure-quelle">
          <option value="widerruf">Widerruf (14 Tage Frist)</option>
          <option value="manuell">Reklamation (auch personalisiert, Grund Pflicht)</option>
        </select>
      </Feld>
      <Feld label="Grund"><input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Gefällt nicht" /></Feld>
    </DialogFormular>
  );
}

function RetourePanel({ r, onSchliessen }: { r: RetoureZeile; onSchliessen: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [zustand, setZustand] = useState<Record<string, RetoureZustand | ''>>({});
  const [freigabe, setFreigabe] = useState(false);
  const [labelDialog, setLabelDialog] = useState(false);
  useEffect(() => { setZustand(Object.fromEntries(r.positionen.map((p) => [p.id, p.zustand ?? '']))); }, [r]);
  const neu = () => { void qc.invalidateQueries({ queryKey: ['retouren'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); void qc.invalidateQueries({ queryKey: ['artikel'] }); void qc.invalidateQueries({ queryKey: ['belege'] }); };
  // N3/R74: bezahlter Betrag nach Rabatt, rechnet der Server (Fallback fuer aeltere API: Preis je Stueck).
  const vorschlag = r.erstattung_vorschlag_cents ?? r.positionen.reduce((s, p) => s + p.einzel_brutto_cents * p.menge, 0);

  async function eingang() {
    try {
      const pos = r.positionen.map((p) => ({ return_item_id: p.id, zustand: zustand[p.id] as RetoureZustand }));
      if (pos.some((p) => !p.zustand)) { toast('Zustand je Position wählen', 'fehler'); return; }
      await retoureEingang(r.id, { positionen: pos });
      toast('Eingang gebucht'); neu();
    } catch (e) { toast((e as Error).message, 'fehler'); }
  }
  return (
    <Detailpanel titel={<span className="mono">{r.nummer}</span>} onSchliessen={onSchliessen}>
      <div className="zeile"><RetoureBadge status={r.status} /> <span className="hinweis">Auftrag {r.bestellnummer} · {r.kunde_name}</span></div>
      <dl className="werte">
        <dt>Grund</dt><dd>{r.grund ?? '–'}</dd>
        <dt>Quelle</dt><dd>{RETOURE_QUELLE_TEXT[r.quelle] ?? r.quelle}</dd>
        <dt>Frist</dt><dd>{datum(r.frist_at)}</dd>
        <dt>Eingang</dt><dd>{datum(r.eingegangen_at)}</dd>
        <dt>Erstattung</dt><dd>{euro(r.erstattung_cents)}</dd>
      </dl>
      <h3>Positionen</h3>
      <ul className="liste-einfach">
        {r.positionen.map((p) => (
          <li key={p.id} className="retoure-pos">
            <span>{p.menge}× {p.name}{p.personalisiert && <small className="warn-text"> personalisiert</small>}</span>
            {r.status === 'angemeldet' ? (
              <select value={zustand[p.id] ?? ''} onChange={(e) => setZustand({ ...zustand, [p.id]: e.target.value as RetoureZustand })} aria-label="Zustand" data-testid="retoure-zustand">
                <option value="">Zustand …</option>
                {RETOURE_ZUSTAENDE.map((z) => <option key={z} value={z}>{ZUSTAND_TEXT[z]}</option>)}
              </select>
            ) : <span className="badge neutral">{p.zustand ? ZUSTAND_TEXT[p.zustand] : '–'}</span>}
          </li>
        ))}
      </ul>
      <p className="hinweis">A bucht zurück auf den Festplatz, B auf RET (Mangel), C bucht Zugang und sofort Korrektur „Retoure nicht verkaufsfähig“.</p>
      <div className="hinweis-box" data-testid="retoure-kosten">
        <strong>Rücksendekosten trägt laut AGB der Kunde.</strong> Es gibt kein automatisches Retourenlabel.
        {r.hat_retourenlabel
          ? <> Ausnahme-Label erzeugt {datum(r.retourenlabel_at ?? null)} ({r.retourenlabel_grund}) · <span className="mono">{r.retourenlabel_tracking}</span> · <DateiLink pfad={retourenlabelPfad(r.id)} testid="retourenlabel-pdf">PDF</DateiLink></>
          : r.status === 'angemeldet' && <Darf rolle="leitung"> <button type="button" className="knopf-zweit klein" onClick={() => setLabelDialog(true)} data-testid="retourenlabel-ausnahme">Retourenlabel als Ausnahme …</button></Darf>}
      </div>
      <div className="knopfreihe links">
        {r.status === 'angemeldet' && <button type="button" className="knopf" onClick={eingang} data-testid="retoure-eingang">Eingang buchen</button>}
        {r.status === 'eingegangen' && <button type="button" className="knopf-zweit" onClick={async () => { try { await retourePruefung(r.id); toast('Geprüft'); neu(); } catch (e) { toast((e as Error).message, 'fehler'); } }}>Prüfung abschließen</button>}
        {r.status === 'geprueft' && <Darf rolle="leitung"><button type="button" className="knopf" onClick={() => setFreigabe(true)} data-testid="retoure-freigabe">Erstattung freigeben</button></Darf>}
      </div>
      {r.status === 'erstattet' && (
        <p className="hinweis-box" data-testid="retoure-erstattung-hinweis">
          Erstattung über {euro(r.erstattung_cents)} freigegeben. Erstattung in WooCommerce auslösen, Gutschrift folgt automatisch.
        </p>
      )}
      <Dialog offen={labelDialog} titel="Retourenlabel als Ausnahme" onSchliessen={() => setLabelDialog(false)}>
        {labelDialog && <AusnahmeFormular onAbbrechen={() => setLabelDialog(false)} onAbsenden={async (grund) => { await retourenlabelErzeugen(r.id, grund); setLabelDialog(false); toast('Retourenlabel erzeugt und per Mail an den Kunden geschickt.'); neu(); }} />}
      </Dialog>
      <Dialog offen={freigabe} titel="Erstattung freigeben" onSchliessen={() => setFreigabe(false)}>
        {freigabe && <FreigabeFormular vorschlag={vorschlag} onAbbrechen={() => setFreigabe(false)} onAbsenden={async (c) => { await erstattungFreigeben(r.id, c); setFreigabe(false); toast('Erstattung freigegeben. Jetzt in WooCommerce erstatten, die Gutschrift folgt automatisch.'); neu(); }} />}
      </Dialog>
    </Detailpanel>
  );
}

function FreigabeFormular({ vorschlag, onAbbrechen, onAbsenden }: { vorschlag: number; onAbbrechen: () => void; onAbsenden: (cents: number) => Promise<void> }) {
  const [betrag, setBetrag] = useState((vorschlag / 100).toFixed(2).replace('.', ','));
  return (
    <DialogFormular absendenText="Freigeben" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      const c = Math.round(Number(betrag.replace(',', '.')) * 100);
      if (!c || c <= 0) throw new Error('Betrag angeben.');
      await onAbsenden(c);
    }}>
      <Feld label="Erstattungsbetrag brutto (EUR)" hinweis="Erstattung in WooCommerce auslösen, Gutschrift folgt automatisch."><input autoFocus value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" data-testid="freigabe-betrag" /></Feld>
      <p className="hinweis">Die Freigabe ist eine Entscheidung, kein Beleg: StoreOS legt eine private Notiz in WooCommerce an. Sobald dort erstattet ist, entsteht die Gutschrift mit Bezug auf diese Retoure.</p>
    </DialogFormular>
  );
}

function AusnahmeFormular({ onAbbrechen, onAbsenden }: { onAbbrechen: () => void; onAbsenden: (grund: string) => Promise<void> }) {
  const [grund, setGrund] = useState('');
  return (
    <DialogFormular absendenText="Label erzeugen und mailen" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      if (grund.trim().length < 5) throw new Error('Grund ist Pflicht (z. B. Reklamation, Falschlieferung, Kulanz).');
      await onAbsenden(grund.trim());
    }}>
      <p className="hinweis">Laut AGB trägt der Kunde die Rücksendekosten. Ein Label auf unsere Kosten ist eine Ausnahme und wird mit Grund protokolliert. Der Kunde bekommt es per Mail (Du-Form).</p>
      <Feld label="Grund (Pflicht)"><input autoFocus value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Reklamation: Ecke beschädigt" data-testid="retourenlabel-grund" /></Feld>
    </DialogFormular>
  );
}
